import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { terminateContractCustomer } from '@/modules/Contract/services/customer'
import { CustomerContract } from '@/modules/Contract/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm, Typogaphy } from 'vnetwork-ui'
import { schemaTerminateContractModal } from './schema'
import { formatPGDate } from '@/utils/function'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface TerminateContractFormValues {
    termination_reason: string
    contract_expired_date: string
    create_termination_appendix: boolean
}

export interface ITerminateContractProps extends ModalProps {
    contractID: string
    contract: CustomerContract
}

const TerminateContractModal = ({
    onClose,
    open,
    contract,
    contractID,
}: ITerminateContractProps) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'contracts',
        mode: 'contains',
    })

    const formControl = useForm<TerminateContractFormValues>({
        defaultValues: {
            termination_reason: '',
            contract_expired_date: contract?.contract_expiry_date || '',
            create_termination_appendix: false,
        },
        resolver: yupResolver(schemaTerminateContractModal as any),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: terminateContractCustomer,
        },
        {
            successMessage: t('terminate-contract-success'),
            reactQueryKeys: QUERY_KEYS.CONTRACTS,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const onSubmit = async (values: TerminateContractFormValues) => {
        await mutateAsync({
            contractID: Number(contractID),
            data: {
                termination_reason: values.termination_reason,
                contract_expired_date:
                    formatPGDate(values.contract_expired_date) || '',
                create_termination_appendix: values.create_termination_appendix,
            },
        })
    }

    return (
        <Dialog title={t('terminate_contract')} onClose={onClose} open={open}>
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <Typogaphy className="text-red-700 font-medium text-sm">
                    ⚠️ {t('terminate_contract_warning')}
                </Typogaphy>
                <Typogaphy className="text-red-600 text-sm mt-2">
                    {t('terminate_contract_description')}
                </Typogaphy>
            </div>

            <GridHookForm
                className="!gap-2"
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('terminate_contract'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                onSubmit={onSubmit}
                data={[
                    {
                        formItemProps: {
                            name: 'contract_expired_date',
                            type: 'datepicker',
                            label: t('contract_expiry_date'),
                            placeholder: t('contract_expiry_date_placeholder'),
                            defaultValue: contract?.contract_expiry_date || '',
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'create_termination_appendix',
                            type: 'checkbox',
                            label: t('create_termination_appendix'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'termination_reason',
                            type: 'textarea',
                            label: t('termination_reason'),
                            placeholder: t('termination_reason_placeholder'),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default TerminateContractModal
